import { GenericModelApi, FetchOpts } from "./GenericModelApi";
import { ApiClient, ApiError, ApiSchema, FetchResponse } from "./ApiClient";
import { getTenantId } from "@utils/Auth";
import { InvoiceFormData, InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { InvoiceProductApi, InvoiceProductDataType } from "./InvoiceProductApi";
import { InvoicePaymentApi, InvoicePaymentDataType } from "./InvoicePaymentApi";
import { omitProperties } from "@utils/Utils";
import { DocumentSequenceApi } from "./DocumentSequenceApi";
import { DocumentAutoGeneratedNumberApi } from "./DocumentAutoGeneratedNumberApi";
import { INVOICE_CONTADO_TYPE_CODE, INVOICE_HOLDING_TYPE_CODE } from "@utils/Constants";

import { matchesAmplifyFilter, removeNullProperties } from "@utils/Api";
import { ProductApi } from "./ProductsApi";
import { saveEDocumentViaLambda } from "./EDocumentSaveApi";

export type InvoiceDataType = Partial<ApiSchema["Invoice"]["type"] & InvoiceFormData>;

class Api extends GenericModelApi<"Invoice", InvoiceDataType> {
    protected userSortKeyDateField = "fecha" as const

    async fetchAll(filters: any = {}, token?: string, limit: number = 20, opts: FetchOpts = {}): Promise<FetchResponse<InvoiceDataType>> {
        const selectionSet = [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "invoiceProducts.*",
            "invoiceProducts.product.*",
        ]

        return super.fetchAll(filters, token, limit, selectionSet, opts);
    }

    /**
     * Claves de Hacienda de todas las facturas del tenant.
     *
     * La carga masiva de XML las necesita completas para marcar duplicados antes de escribir.
     * Se pagina con el selectionSet minimo y sin cache porque solo interesa `invoice_key`.
     */
    async fetchAllDocumentKeys(): Promise<Set<string>> {
        const keys = new Set<string>();
        let token: string | undefined = undefined;

        do {
            const { items, nextToken }: FetchResponse<InvoiceDataType> = await super.fetchAll(
                {},
                token,
                1000,
                ["id", "invoice_key"],
                { noCache: true }
            );
            items.forEach((invoice) => {
                if (invoice.invoice_key) keys.add(`${invoice.invoice_key}`);
            });
            token = nextToken;
        } while (token);

        return keys;
    }

    /**
     * Facturas del tenant en la forma minima que necesita la carga de notas para ligarlas.
     *
     * `Invoice` no tiene indice por `sequence` ni por `invoice_key`, asi que la unica manera de
     * resolver una referencia es indexarlas en memoria; por eso el selectionSet se limita a lo
     * que la nota hereda o compara.
     */
    async fetchAllReferenceable(): Promise<InvoiceDataType[]> {
        const invoices: InvoiceDataType[] = [];
        let token: string | undefined = undefined;

        do {
            const { items, nextToken }: FetchResponse<InvoiceDataType> = await super.fetchAll(
                {},
                token,
                1000,
                ["id", "invoice_key", "sequence", "numero", "clientId", "status", "fecha"],
                { noCache: true }
            );
            invoices.push(...items);
            token = nextToken;
        } while (token);

        return invoices;
    }

    async fetchAllForStatus(clientId: string, statusFilter: string, filters: any = {}, token?: string, limit: number = 1000): Promise<FetchResponse<InvoiceDataType>> {
        const selectionSet = [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "creditnotes.*",
            "debitnotes.*",
            "invoicePayments.*",
            "interestEntries.*",
            "apliedCreditnote.*",
            "recibe.*"
        ]
        const tenantId = await getTenantId();
        const fetchFunction = ApiClient.models[this.modelName].listInvoiceByStatusClientTenant;

        const collected: InvoiceDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction({
                owner: tenantId,
                statusClientId: {
                    beginsWith: {
                        status: statusFilter,
                        clientId: clientId
                    }
                }
            }, {
                sortDirection: "ASC",
                limit: limit,
                nextToken,
                selectionSet: selectionSet,
            });

            if (errors) {
                throw new ApiError("Error al obtener facturas por tenant", "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: InvoiceDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };

    }

    async fetchAllByClientDate(clientId: string, endDate: string, filters: any = {}, token?: string, limit: number = 1000, sortDirection: "ASC" | "DESC" = "ASC"): Promise<FetchResponse<InvoiceDataType>> {
        const selectionSet = [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "creditnotes.*",
            "debitnotes.*",
            "invoicePayments.*",
            "interestEntries.*",
            "apliedCreditnote.*",
            "recibe.*"
        ];

        const tenantId = await getTenantId();
        const fetchFunction = ApiClient.models[this.modelName].listInvoiceByClientFechaTenant;

        const fetchProps = {
            owner: tenantId,
            clientIdFecha: {
                between: [
                    {
                        fecha: "1900-01-01",
                        clientId: clientId
                    },
                    {
                        fecha: endDate,
                        clientId: clientId
                    }
                ]
            }
        };

        const collected: InvoiceDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction(fetchProps, {
                sortDirection,
                limit,
                nextToken,
                selectionSet,
            });

            if (errors) {
                throw new ApiError("Error al obtener facturas por cliente y fecha", "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: InvoiceDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };
    }

    async fetchAllByRecibe(recibeId: string): Promise<FetchResponse<InvoiceDataType>> {
        const fetchFunction = ApiClient.models[this.modelName].listInvoiceByRecibeId;

        const { data, errors, nextToken: nt } = await fetchFunction({
            recibeId: recibeId
        }, {
            limit: 1000
        });

        if (errors) {
            throw new ApiError("Error al obtener Facturas por recibo", "API_ERROR", errors);
        }

        return { items: data };
    }

    async fetchAllByInvoiceNumber(invoiceNumber: number): Promise<FetchResponse<InvoiceDataType>> {
        const fetchFunction = ApiClient.models[this.modelName].listInvoiceByTenant;
        const tenantId = await getTenantId();

        const selectionSet = [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "creditnotes.*",
            "debitnotes.*",
            "invoicePayments.*",
            "interestEntries.*",
            "apliedCreditnote.*",
            "recibe.*"
        ]

        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            numero: {
                eq: invoiceNumber
            }
        }, {
            limit: 1000,
            selectionSet
        });

        if (errors) {
            throw new ApiError("Error al obtener Facturas por recibo", "API_ERROR", errors);
        }

        return { items: data };
    }

    /**
     * El indice `invoicesByOwnerAndSalemanIdAndFecha` usa un sort key compuesto
     * `salemanId#fecha`. El resolver de AppSync exige que un update que toque UNA parte
     * de esa llave mande TAMBIEN la otra, o falla con:
     *
     *   "When updating any part of the composite sort key for @index
     *    'invoicesByOwnerAndSalemanIdAndFecha', you must provide all fields for the key."
     *
     * Muchos flujos actualizan `fecha` sin saber nada del vendedor (aplicar un recibo,
     * anular un recibo, marcar como pagada). En lugar de obligar a cada uno a acordarse,
     * se completa aqui: se lee el `salemanId` vigente y se reenvia tal cual, incluso si
     * es null (mandar la llave en null es valido y deja la factura fuera del indice, que
     * es justo lo que corresponde cuando no tiene vendedor).
     */
    async update(id: string, input: Partial<Omit<InvoiceDataType, "id">>, allowNulls = false): Promise<InvoiceDataType> {
        const touchesKey = "fecha" in input || "salemanId" in input;
        if (!touchesKey || "salemanId" in input) {
            return super.update(id, input, allowNulls);
        }

        const payload = (allowNulls ? { ...input } : removeNullProperties({ ...input })) as Partial<InvoiceDataType>;
        const current = await this.get(id, { selectionSet: ["id", "salemanId"] });
        payload.salemanId = current?.salemanId ?? null;

        // allowNulls para que `salemanId: null` sobreviva: es la unica forma de satisfacer
        // la validacion del resolver en facturas sin vendedor asignado.
        return super.update(id, payload, true);
    }

    /**
     * `includeCategory` agrega la categoria del producto de cada linea. Se pide bajo
     * demanda porque resolver el `belongsTo` de Product en cada linea es caro y solo
     * lo necesita el reporte de ventas por cliente cuando agrupa por categoria.
     *
     * `salemanId` restringe el resultado al vendedor de la factura. Sin `clientId` usa su
     * propio indice (`listInvoiceBySalemanFechaTenant`); con ambos consulta por cliente y
     * descarta en memoria.
     */
    async fetchAllByClientPeriod(clientId: string, startData: string, endData: string, filters: any = {}, token?: string, limit: number = 1000, sortDirection: string = "DESC", includeCategory: boolean = false, salemanId: string = ""): Promise<FetchResponse<InvoiceDataType>> {
        const selectionSet = [
            "id",
            "anulada",
            "edocument_status_payload",
            "exchange_rate",
            "salemanId",
            "currency.currency_id",
            "invoiceProducts.codigo",
            "invoiceProducts.description",
            "invoiceProducts.cant",
            "invoiceProducts.codigo_cabys",
            "invoiceProducts.productId",
            "invoiceProducts.subtotal",
            "invoiceProducts.descuento",
            ...(includeCategory ? ["invoiceProducts.product.id", "invoiceProducts.product.categoryId"] : []),
        ]

        const tenantId = await getTenantId();
        let fetchFunction;
        let fetchProps;
        // Cuando vienen cliente y vendedor a la vez se consulta por el indice de cliente y
        // el vendedor se descarta en memoria: DynamoDB solo permite un sort key compuesto
        // por consulta, y filtrar dentro de las facturas de un cliente es mucho mas barato
        // que hacerlo dentro de las de un vendedor.
        let periodFilters = filters;
        if (clientId) {
            if (salemanId) {
                periodFilters = { ...filters, salemanId: { eq: salemanId } };
            }
            fetchFunction = this.model.listInvoiceByClientFechaTenant;
            fetchProps = {
                owner: tenantId,
                clientIdFecha: {
                    between: [
                        {
                            fecha: startData,
                            clientId: clientId
                        },
                        {
                            fecha: endData,
                            clientId: clientId
                        }
                    ]
                }
            };
        } else if (salemanId) {
            fetchFunction = this.model.listInvoiceBySalemanFechaTenant;
            fetchProps = {
                owner: tenantId,
                salemanIdFecha: {
                    between: [
                        {
                            fecha: startData,
                            salemanId: salemanId
                        },
                        {
                            fecha: endData,
                            salemanId: salemanId
                        }
                    ]
                }
            };
        } else {
            fetchFunction = this.model.listInvoiceByFechaTenant;
            fetchProps = {
                owner: tenantId,
                fecha: { between: [startData, endData] }
            };
        }

        const collected: InvoiceDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction(fetchProps, {
                sortDirection,
                limit,
                nextToken,
                selectionSet,
            });

            if (errors) {
                throw new ApiError(`Error al obtener los  ${String(this.modelName)} por client y period tenant`, "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: InvoiceDataType) => matchesAmplifyFilter(item, periodFilters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };
    }

    async fetchAllByPeriod(startData: string, endData: string, filters: any = {}, token?: string, limit: number = 500): Promise<FetchResponse<InvoiceDataType>> {
        const selectionSet = [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "creditnotes.*",
            "creditnotes.creditnoteProducts.*",
            "creditnotes.currency.*",
            "debitnotes.*",
            "debitnotes.debitnoteProducts.*",
            "debitnotes.currency.*",
            "invoiceProducts.*",
            "invoicePayments.*",
            "interestEntries.*",
            "recibe.*"
        ]
        return await super.fetchAllByPeriod("listInvoiceByFechaTenant", startData, endData, filters, token, {
            selectionSet,
            limit
        })

    }

    async searchByInvoiceNumber(
        productCode: string,
        selectionSet?: any
    ): Promise<FetchResponse<InvoiceDataType>> {
        const tenantId = await getTenantId();

        const fetchFunction = ApiClient.models[this.modelName][`list${this.modelName}ByTenant`];
        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            numero: { eq: productCode }
        }, {
            limit: 1,
            selectionSet: selectionSet,
        });

        if (errors) {
            throw new ApiError("Error al obtener la factura por numero", "API_ERROR", errors);
        }

        const pageItems = (data || [])
        return {
            items: pageItems,
            nextToken: nt
        }
    }

    /**
     * Guardado transaccional en una sola llamada (lambda saveEDocument).
     * Secuencias, clave de Hacienda, pagos y reducción de stock ocurren de forma
     * atómica en el backend; el retorno mantiene la misma forma para que el resto
     * del flujo del form no cambie.
     */
    async createInvoiceAndProducts(input: InvoiceDataType, products: InvoiceProductDataType[], payments: InvoicePaymentDataType[]): Promise<InvoiceDataType> {
        // El form puede declarar una condicion de venta distinta al tipo (un contado con
        // no_auto_cancel_contado se emite como credito); si no la declara, se deriva del tipo.
        const saleCondition = Number(input.saleCondition ?? (input.es_apartado ? 2 : input.tipo))

        const documentData = {
            ...input,
            status: input.status || "pendiente",
            saleCondition
        }

        const lines = products.map((p) => {
            const product = omitProperties({ ...p }, ["id", "forceRecalculatePrice"], true);
            if (product.exemption && typeof product.exemption == "object") {
                product.exemption = JSON.stringify(product.exemption);
            }
            return product;
        })

        const includePayments = documentData.tipo == INVOICE_CONTADO_TYPE_CODE || documentData.tipo == INVOICE_HOLDING_TYPE_CODE
        const invoicePayments = includePayments
            ? payments.map((p) => omitProperties({ ...p }, ["id"], true))
            : []

        const data = await saveEDocumentViaLambda<InvoiceDataType>({
            documentType: "Invoice",
            document: documentData as Record<string, unknown>,
            lines,
            payments: invoicePayments,
            stockMode: "reduce",
        })

        this.invalidateAllByTenant()
        this.clearCache()
        InvoiceProductApi.invalidateAllByTenant()
        InvoicePaymentApi.invalidateAllByTenant()
        ProductApi.invalidateAllByTenant()
        DocumentSequenceApi.invalidateAllByTenant()
        DocumentAutoGeneratedNumberApi.invalidateAllByTenant()

        return data;
    }
}
const api = new Api("Invoice", "sequence", "DESC")
api.setCreateDataFieldName("fecha")
export const InvoiceApi = api